import { ArrayDataSource } from "@angular/cdk/collections";
import { CdkTreeModule, NestedTreeControl } from "@angular/cdk/tree";
import { Component, ElementRef, Inject, Renderer2 } from "@angular/core";
import { faLaptopCode, faLightbulb, faMagic, faAngleRight, faAngleUp, faAngleDown, faMinus, faPlus } from "@fortawesome/free-solid-svg-icons";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FaIconComponent } from "@fortawesome/angular-fontawesome";
import { TrvFormBundle, TrvLoadingBundle, TrvModalService, TrvTableBundle, TrvTooltipDirective, TrvTooltipPosition } from "@trv-hub/trv-ng-common";
import { FormControl, FormsModule, NgForm, NgModel, ReactiveFormsModule } from "@angular/forms";
import { SvgIcon } from "@app/shared/utils/svg-helper";
import { svgInfo, svgMagnifyingGlass, svgMagnifyingPlusFilled, svgWarning } from "@app/shared/utils/svg-helper-data";
import { SvgIconComponent } from "@app/components/utils/svg-icon/svg-icon.component";
import * as yaml from "js-yaml";
import { HttpClient } from "@angular/common/http";
import { debounceTime, distinctUntilChanged, firstValueFrom, map } from "rxjs";
import { LayerListItem, TrvLayer, TrvMap, TrvNgMapService } from "@trafikverket/trv-ng-map";
import { LayerInfoModalComponent } from "@app/components/map-menu/layer-section/layer-info-modal/layer-info-modal.component";
import { ApmService } from "@app/service/apm.service";
import { NvdbNavigationService } from "@app/service/nvdb-navigation.service";
import { NetDbService } from "@app/service/net-db.service";
import { Language, LanguageService } from "@app/service/language.service";
import { CommonModule } from "@angular/common";

@Component({
    selector: "app-dataslag-tree",
    imports: [
        FaIconComponent,
        CdkTreeModule,
        TrvTableBundle,
        FormsModule,
        SvgIconComponent,
        TrvTooltipDirective,
        TrvLoadingBundle,
        TrvFormBundle,
        CommonModule,
        ReactiveFormsModule
    ],
    templateUrl: "./dataslag-tree.component.html",
    styleUrl: "./dataslag-tree.component.scss",
})
export class DataslagTreeComponent {
    public faLightbulb: IconProp = faLightbulb as IconProp;
    public faAngleDown: IconProp = faAngleDown as IconProp;
    public faAngleRight: IconProp = faAngleRight as IconProp;
    public faPlus: IconProp = faPlus as IconProp;
    public faMinus: IconProp = faMinus as IconProp;
    public faMagic: IconProp = faMagic as IconProp;
    public faLaptopCode: IconProp = faLaptopCode as IconProp;
    public svgWarning: SvgIcon = svgWarning;
    public searchIcon: SvgIcon = svgMagnifyingGlass;


    public searchIconPlusFilled: SvgIcon = svgMagnifyingPlusFilled;
    public infoIcon: SvgIcon = svgInfo;

    public toolTipPositions: TrvTooltipPosition[] = ["bottom", "right"];

    treeControl!: NestedTreeControl<LayerNode>;
    dataSource!: ArrayDataSource<LayerNode>;
    counter = 1;

    public isLoadingTree = true;

    //key value metakey to node
    public metakeyToNode: { [key: string]: LayerNode } = {};

    public njdbDataslagsFilters: JnbDataslagFilter[] = [
        {
            id: 0,
            name: "2024",
        },
        {
            id: 1,
            name: "2025",
        },
        {
            id: 2,
            name: "2026",
        },
    ];

    public dataslagSearch = new FormControl("", { nonNullable: true });
    public showChoosenDataslag = new FormControl(false, { nonNullable: true });

    private rootNodes: LayerNode[] = [];
    private savedExpansionForSearch: LayerNode[] | null = null;
    private pinnedByShowChosen = new Set<LayerNode>(); // keep nodes visible while "Visa valda" is on


    constructor(
        private elementRef: ElementRef,
        private renderer: Renderer2,
        private http: HttpClient,
        public trvMapService: TrvNgMapService,
        @Inject(TrvModalService) private trvModalService: TrvModalService,
        @Inject(NetDbService) private netDbService: NetDbService,
        @Inject(ApmService) private apmService: ApmService,
        public nvdbNavigation: NvdbNavigationService,
        public lang: LanguageService
    ) {

        if (!trvMapService.trvMap) return;
        const trvMap = trvMapService.trvMap!;
        const trvLayer = trvMap.trvLayer;

        let layer = this.getLayerFromMetakey("GDI:BANDELAR:");
        if (layer) {
            trvMapService.trvMap?.trvLayer.setLayerVisibility(layer, true);
        }

        this.reloadYmlTree();

        this.trvMapService.trvMap?.trvLayer.layerVisibilityUpdated.subscribe(layers => {
            //console.log("layerVisibilityUpdated", layers);
            layers.forEach(layer => {
                if (trvLayer.hasLayerData(layer, "metaKey")) {
                    const metakey = trvLayer.getLayerData(layer, "metaKey");
                    const node = this.metakeyToNode[metakey];
                    if (!node) return;
                    if (node.selected == true) {
                        if (layer.active == false) {
                            node.selected = false;
                            this.onNodeSelected(node, "", false, false);
                        }
                    } else {
                        if (layer.active == true) {
                            node.selected = true;
                            this.onNodeSelected(node, "", false, false);
                        }
                    }
                }
            });
        });

        // Expand branches with active leaves when filter is turned on
        this.showChoosenDataslag.valueChanges.subscribe(show => {
            if (show) {
                // pin all currently active leaves and their ancestors
                this.pinCurrentlyActiveBranches();
                // expand to reveal pinned/active branches
                this.expandBranchesWithActiveOrPinned();
            } else {
                // clear pins when turning off
                this.pinnedByShowChosen.clear();
            }
        });

        // If visibility changes while filter is on, re-expand relevant branches
        // If visibility changes while filter is on, re-expand and pin newly activated
        this.trvMapService.trvMap?.trvLayer.layerVisibilityUpdated.subscribe(layers => {
            if (this.showChoosenDataslag.value) {
                // pin nodes that just became active; do not unpin if they became inactive
                const trvLayer = this.trvMapService.trvMap!.trvLayer;
                layers.forEach(layer => {
                    if (trvLayer.hasLayerData(layer, 'metaKey')) {
                        const metakey = trvLayer.getLayerData(layer, 'metaKey');
                        const node = this.metakeyToNode[metakey];
                        if (node && layer.active) {
                            this.pinNodeAndAncestors(node);
                        }
                    }
                });
                this.expandBranchesWithActiveOrPinned();
            }
        });

        this.dataslagSearch.valueChanges
            .pipe(debounceTime(150), distinctUntilChanged())
            .subscribe(termRaw => {
                const term = (termRaw ?? '').trim().toLowerCase();

                if (term) {
                    if (!this.savedExpansionForSearch) {
                        this.savedExpansionForSearch = [...this.treeControl.expansionModel.selected];
                    }
                    this.expandBranchesWithNameMatch(term);
                } else if (this.savedExpansionForSearch) {
                    // restore previous expansion when search is cleared
                    this.treeControl.collapseAll();
                    this.savedExpansionForSearch.forEach(n => this.treeControl.expand(n));
                    this.savedExpansionForSearch = null;
                }
            });
    }

    public getTrvMap(): TrvMap | undefined {
        return this.trvMapService.trvMap;
    }

    public numberOfDataProdukterActive() {
        const trvMap = this.trvMapService.trvMap!;
        const trvLayer = trvMap.trvLayer;
        let dataprod = this.trvMapService.trvMap!.trvLayer.getVisibleWmsLayer().filter(layer => trvLayer.getLayerData(layer, "showInLayer") != true);
        return dataprod.length;
    }

    public disableAllLayers() {
        const trvMap = this.trvMapService.trvMap!;
        const trvLayer = trvMap.trvLayer;
        trvLayer.wmsLayerListItems.forEach(layer => {
            if (layer.active && trvLayer.getLayerData(layer, "showInLayer") != true) {
                trvLayer.setLayerVisibility(layer, false);
            }
        });
    }


    hasChild = (_: number, node: LayerNode) => !!node.children && node.children.length > 0;

    onNodeSelected = (node: LayerNode, treeId: string, skipChildren?: boolean, skipParent?: boolean) => {
        const trvMap = this.trvMapService.trvMap!;

        if (node.children && !skipChildren) {
            node.children?.forEach(c => {
                c.selected = node.selected;
                this.onNodeSelected(c, treeId, false, true);
            });
        }
        if (node.parent && !skipParent) {
            const allSelected = node.parent.children?.every(c => c.selected === true);
            if (node.parent.selected !== allSelected) {
                node.parent.selected = allSelected;
                this.onNodeSelected(node.parent, treeId, true);
            }
            const anySelected = node.parent.children?.some(c => c.selected === true);
            const parentCheckboxElement = document.getElementById("node-separate" + treeId + "-" + node.parent.id);
            if (anySelected && !allSelected) {
                this.renderer.setProperty(parentCheckboxElement, "indeterminate", true);
            } else {
                this.renderer.setProperty(parentCheckboxElement, "indeterminate", false);
            }
        }

        if (node.layer) {
            if (node.selected) {
                trvMap.trvLayer.setLayerVisibility(node.layer, true);
            } else {
                trvMap.trvLayer.setLayerVisibility(node.layer, false);
            }
        }
    };

    public reloadYmlTree() {
        this.yamlToTree().subscribe(yml => {
            yml.forEach(node => {
                this.setAttributes(node);
            });
            this.treeControl = new NestedTreeControl<LayerNode>(node => node.children);
            this.dataSource = new ArrayDataSource(yml);
            this.rootNodes = yml;
            this.isLoadingTree = false;
        });
    }

    private yamlToTree() {
        return this.http.get("assets/data/layer-data/jnb-tree.yml", { responseType: "text" }).pipe(
            map(yamlText => {
                const parsed = yaml.load(yamlText) as { tree: any[] };
                return this.convertToLayerNodes(parsed.tree);
            })
        );
    }

    public getLayerFromMetakey(metakey: string) {
        const trvMap = this.trvMapService.trvMap!;

        let layer = trvMap.trvLayer.wmsLayerListItems.find((layer: LayerListItem) => {
            if (trvMap.trvLayer.hasLayerData(layer, "metaKey")) {
                if (trvMap.trvLayer.getLayerData(layer, "metaKey") == metakey) {
                    return layer;
                }
            }
            return undefined;
        });

        return layer;
    }

    private convertToLayerNodes(data: any[], parent: LayerNode | null = null): LayerNode[] {
        return data.map(node => {
            const layerNode: LayerNode = {
                parent: parent ?? undefined,
            };

            if (node.name) {
                layerNode.name = node.name;
            }

            if (node.metakey) {
                layerNode.metakey = node.metakey;
            }

            if (node.children) {
                layerNode.children = this.convertToLayerNodes(node.children, layerNode);
            }

            return layerNode;
        });
    }

    private setAttributes = (node: LayerNode, level = 0, parent?: LayerNode): void => {
        node.id = this.counter;
        this.counter++;
        node.parent = parent;
        node.level = level;
        node.children?.forEach(c => this.setAttributes(c, level + 1, node));
        if (node.metakey) {
            node.layer = this.getLayerFromMetakey(node.metakey);

            this.metakeyToNode[node.metakey] = node;

            if (node.layer && node.layer.active) {
                node.selected = true;
                this.onNodeSelected(node, "");
            } else if (node.layer && !node.layer.active) {
                node.selected = false;
                this.onNodeSelected(node, "");
            }
        }
    };

    /**
     * Metod som triggras när ett dataslag har blivit högerklickat på.
     * @param event
     * @param layer Det dataslag som klickats på
     * @returns false;
     */
    public onRightClick(event: any, layer: LayerListItem) {
        this.infoAboutLayer(layer);
        return false;
    }

    /**
     * Metod som öppnar modal som visar information om nuvarande lager.
     * @param layer
     */
    public async infoAboutLayer(layer: LayerListItem) {
        this.apmService.renameCurrentTransaction("Open info modal");

        const modal = this.trvModalService.open(LayerInfoModalComponent, {
            disposeOnBackdropClick: true,
            size: this.nvdbNavigation.isMobileDevice ? "fullscreen" : "md",
        });
        const component = modal.componentInstance;
        component.title = layer.name;

        let metaKey: string = "";
        if (layer.trvData.hasOwnProperty("metaKey")) metaKey = (layer.trvData as any).metaKey;

        if (layer.trvData.hasOwnProperty("description")) {
            layer.description = (layer.trvData as any).description;
        } else {
            if (metaKey != "") {
                try {
                    var desc = await firstValueFrom(this.netDbService.getDescription(metaKey));
                    desc = desc.replace("\n", "<br>");
                    layer.description = desc;
                } catch (e) {}
            }
        }

        component.text = layer.description ? layer.description : this.lang.GetValue("Layerlist.LacksDescrip");
        component.layerVisibleFrom = layer.maxScaleDenominator ? Math.round(layer.maxScaleDenominator) : layer.maxScaleDenominator;
        component.legendGraphics = layer.legendGraphics;
        component.layer = layer;

        modal.afterCloseWithType().subscribe(closeParams => {
            //console.log(closeParams)
        });
    }

    public displayObjectNjdbFilter = (njdbFilter: JnbDataslagFilter) => {
        return njdbFilter.name;
    };

    public getDisplayName(layer: LayerListItem): string {
        const trvMap = this.trvMapService.trvMap!;
        const trvLayer = trvMap.trvLayer;
        if (this.lang.currentLanguage == Language.SV && trvLayer.hasLayerData(layer, "sweTitle")) {
            const displayName = trvLayer.getLayerData(layer, "sweTitle");
            return displayName;
        } else if (this.lang.currentLanguage == Language.ENG && trvLayer.hasLayerData(layer, "engTitle")) {
            const displayName = trvLayer.getLayerData(layer, "engTitle");
            return displayName;
        }
        return layer.name;
    }

    public hasAnyActiveDescendant(node: LayerNode | undefined): boolean {
        if (!node) return false;
        if (node.layer) return !!node.layer.active;
        return node.children?.some(child => this.hasAnyActiveDescendant(child)) ?? false;
    }

    private expandBranchesWithActive(): void {
        if (!this.treeControl || !this.rootNodes?.length) return;

        const visit = (node: LayerNode) => {
            const hasActive = this.hasAnyActiveDescendant(node);
            if (hasActive) {
                this.treeControl.expand(node);
                node.children?.forEach(visit);
            }
        };

        this.rootNodes.forEach(visit);
    }

    // Hide logic combining "Visa valda" and search
    public shouldHideNode(node: LayerNode): boolean {
        const showOnlySelected = this.showChoosenDataslag.value;
        const term = (this.dataslagSearch.value ?? "").trim().toLowerCase();

        const passesSelected =
            !showOnlySelected || this.hasAnyActiveDescendant(node) || this.hasPinnedDescendant(node);
        const passesSearch = term.length === 0 || this.hasNameMatch(node, term);

        return !(passesSelected && passesSearch);
    }

    // True if this node (leaf by layer name) or any descendant matches the term
    private hasNameMatch(node: LayerNode, term: string): boolean {
        const isLeafMatch = !!node.layer?.name && node.layer.name.toLowerCase().includes(term);
        if (isLeafMatch) return true;
        return node.children?.some(child => this.hasNameMatch(child, term)) ?? false;
    }

    // Expand all branches that contain a matching descendant
    private expandBranchesWithNameMatch(term: string): void {
        if (!this.treeControl || !this.rootNodes?.length) return;

        const visit = (n: LayerNode) => {
            const hasMatch = this.hasNameMatch(n, term);
            if (hasMatch) {
                this.treeControl.expand(n);
                n.children?.forEach(visit);
            }
        };
        this.rootNodes.forEach(visit);
    }

    public hasAnyVisibleDataProducts(): boolean {
        //console.log(this.rootNodes)
        if (!this.rootNodes?.length) return false;
        const showOnlySelected = this.showChoosenDataslag.value;
        const term = (this.dataslagSearch.value ?? '').trim().toLowerCase();
        return this.rootNodes.some(n => this.hasVisibleLeaf(n, showOnlySelected, term));
    }

    private hasVisibleLeaf(node: LayerNode, showOnlySelected: boolean, term: string): boolean {
        const leafVisible =
            !!node.layer &&
            // visible if active OR pinned when "Visa valda" is on
            (!showOnlySelected || !!node.layer.active || this.pinnedByShowChosen.has(node)) &&
            (term.length === 0 || (node.layer.name?.toLowerCase().includes(term) ?? false));

        if (leafVisible) return true;
        return node.children?.some(c => this.hasVisibleLeaf(c, showOnlySelected, term)) ?? false;
    }

    // Pinned helpers
    private pinNodeAndAncestors(node: LayerNode): void {
        let n: LayerNode | undefined = node;
        while (n) {
            this.pinnedByShowChosen.add(n);
            n = n.parent;
        }
    }

    private pinCurrentlyActiveBranches(): void {
        const visit = (n: LayerNode) => {
            if (n.layer?.active) this.pinNodeAndAncestors(n);
            n.children?.forEach(visit);
        };
        this.rootNodes.forEach(visit);
    }

    private hasPinnedDescendant(node: LayerNode | undefined): boolean {
        if (!node) return false;
        if (this.pinnedByShowChosen.has(node)) return true;
        return node.children?.some(c => this.hasPinnedDescendant(c)) ?? false;
    }

    private expandBranchesWithActiveOrPinned(): void {
        const visit = (n: LayerNode) => {
            const shouldExpand = this.hasAnyActiveDescendant(n) || this.hasPinnedDescendant(n);
            if (shouldExpand) {
                this.treeControl.expand(n);
                n.children?.forEach(visit);
            }
        };
        this.rootNodes.forEach(visit);
    }
}

interface LayerNode {
    name?: string;
    metakey?: string;
    children?: LayerNode[];
    parent?: LayerNode;
    id?: number;
    level?: number;
    selected?: boolean;
    partialSelected?: boolean;
    layer?: LayerListItem;
}

export interface JnbDataslagFilter {
    id: number;
    name: string;
}
